import * as z from "zod";
import { ianaTimeZoneNameSchema } from "../timezone-list";
import { TeamCalendarEntryAgendaItemSchema } from "./agenda/team-calendar-entry-agenda-item";
import { TeamCalendarEntryType } from "./team-calendar-entry-type";

export {
  TeamCalendarEntryAgendaItemSchema,
  TeamCalendarEntryAgendaItemType,
  type TeamCalendarEntryAgendaItem,
} from "./agenda/team-calendar-entry-agenda-item";
export { TeamCalendarEntryType } from "./team-calendar-entry-type";

/**
 * A team calendar entry: a merchant-scheduled or merchant-recorded item
 * (sales, promotions, restocks, holidays, anything else).
 *
 * Entries are mutable plans — they can be edited, rescheduled, or canceled.
 *
 * `type` is a top-level kind owned by a product area (Marketing today).
 *
 * `agenda` is an array of self-contained items bolted onto the entry —
 * promotions, discount offers, tracking page updates, etc. Each item has
 * its own lifecycle hooks (see AgendaItemHandler) and can appear multiple
 * times on a single entry.
 *
 * Lifecycle is intentionally derived, not stored. Whether an entry is
 * upcoming, currently happening, or in the past is purely a function of
 * `(startAt, endAt, now)` and a single explicit signal — `canceledAt`.
 */
const TeamCalendarEntrySchema_ = z.object({
  id: z.string(),
  accountId: z.string(),
  type: z.nativeEnum(TeamCalendarEntryType),
  title: z.string(),
  description: z.string().nullish(),
  startAt: z.date(),
  endAt: z.date(),
  // IANA timezone name (e.g. "America/Los_Angeles"). startAt/endAt are
  // stored as UTC instants; this is the timezone the merchant authored the
  // entry in and how it should be rendered/interpreted (e.g. "midnight" =
  // midnight in this zone).
  timezone: ianaTimeZoneNameSchema,
  allDay: z.boolean(),
  // Set when the merchant explicitly cancels the entry. Null otherwise.
  canceledAt: z.date().nullish(),
  agenda: z.array(TeamCalendarEntryAgendaItemSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const TeamCalendarEntrySchema: z.ZodType<TeamCalendarEntry> =
  TeamCalendarEntrySchema_;

export interface TeamCalendarEntry extends z.infer<
  typeof TeamCalendarEntrySchema_
> {}

/**
 * Whether the entry is currently happening at `at` (defaults to now).
 * Canceled entries never count as active.
 */
export function isTeamCalendarEntryActive(
  entry: Pick<TeamCalendarEntry, "startAt" | "endAt" | "canceledAt">,
  at: Date = new Date(),
): boolean {
  if (entry.canceledAt) {
    return false;
  }
  return entry.startAt <= at && at <= entry.endAt;
}
